import Cookies from 'js-cookie'
import styled, { css } from 'styled-components'

import { useGetRecommendationsQuery } from 'api/productTemplateApi'
import { toProductDataItem } from 'layout/navigation/search/utils'
import { t } from 'localization'
import TrendingProducts from 'productTemplate/components/TrendingProducts'
import { useCallback, useEffect, useRef, useState } from 'react'
import { resolveVariantPrice } from 'server/utils/productTemplate/productVariantUtils'
import { Button } from 'shared/components/Button'
import { LinkCTA } from 'shared/components/Links'
import { GridContent } from 'shared/components/ProductGrid/GridContent'
import ProductGrid from 'shared/components/ProductGrid/ProductGrid'
import Spinner from 'shared/components/Spinner'
import { useShoppingRegionContext } from 'shared/contexts/ShoppingRegionContextProvider'
import { ProductQA } from 'shared/dataAttributes'
import { DEFAULT_CURRENCY_CODE, STORAGE } from 'shared/enums/SitePreferences'
import { fonts, media, styles } from 'shared/lib'
import { IProduct, Price, ProductTemplate } from 'shared/types'
import { ProductDataItem } from 'shared/types/ConsumerSearch'
import { getConsumerSearchFilters } from 'shared/utils/search/cs-utils'

const TP = 'productTemplate.components.ProductRecommendations'

type ProductRecommendationsProps = {
  buttonLabel?: string
  productTemplateId: string
  title?: string
  isPDP?: boolean
}

export const ProductRecommendations = ({
  buttonLabel,
  productTemplateId,
  title = t(`${TP}.recommendedForYou`, 'Recommended For You'),
  isPDP,
}: ProductRecommendationsProps) => {
  // Query to fetch recommendations from Sneakers
  const {
    data: recommendationsData,
    error,
    isLoading,
    refetch: recommendationsRefetch,
  } = useGetRecommendationsQuery(productTemplateId, {
    skip: !productTemplateId,
  })

  const [consumerSearchTrendingResults, setConsumerSearchTrendingResults] = useState<
    ProductDataItem[]
  >([])
  const hasConsumerSearchData = useRef<boolean>(false)
  // Query to fetch trending products from the Consumer Search service
  const getConsumerSearchTrendingProducts = useCallback(async () => {
    const consumerSearchTrendingSearchQuery = getConsumerSearchFilters({
      searchState: {},
      cookies: {
        currency: Cookies.get(STORAGE.CURRENCY) as string,
        country: Cookies.get(STORAGE.COUNTRY) as string,
      },
      queryParams: { sortBy: '5' },
      pageLimit: '8',
    })

    const url =
      '/api/product-search?' + new URLSearchParams(consumerSearchTrendingSearchQuery).toString()

    const { data } = await fetch(url, {
      method: 'GET',
    })
      .then(async (res) => {
        return res.json()
      })
      .catch(() => {
        return {
          data: {},
          error: 'error fetching data',
          total: 0,
        }
      })

    setConsumerSearchTrendingResults(toProductDataItem(data?.productsList || []))
    hasConsumerSearchData.current = true
  }, [])

  const { currencyCode: selectedCurrencyCode, availableCurrencies } = useShoppingRegionContext()
  const [recommendations, setRecommendations] = useState<
    (ProductTemplate & { price: Price | null })[]
  >([])

  // Set recommendations from Sneakers
  useEffect(() => {
    if (isLoading === false && !error) {
      const { productTemplates } = recommendationsData || {}
      const symbol =
        availableCurrencies.find((c) => c.isoCode === selectedCurrencyCode)?.symbol ||
        DEFAULT_CURRENCY_CODE
      const recommendations = productTemplates
        ?.map((pt) => ({
          ...pt,
          price: resolveVariantPrice(pt.localizedLowestPriceCents, symbol),
        }))
        .slice(0, 8) // Limit to 8 items

      setRecommendations(recommendations || [])
    }
  }, [isLoading, error, recommendationsData])

  // Refetch Sneakers recommendations when the selected currency changes.
  useEffect(() => {
    recommendationsRefetch()
  }, [selectedCurrencyCode])

  // Fetch Consumer Search trending products . Refetch when currency changes.
  useEffect(() => {
    if (!hasConsumerSearchData?.current || selectedCurrencyCode) {
      void getConsumerSearchTrendingProducts()
    }
  }, [hasConsumerSearchData, selectedCurrencyCode])

  return (
    <Content>
      <Spinner showSpinner={isLoading}>
        {!isLoading && (
          <GridContent data-testid="productRecommendations">
            <TitleContainer>
              <Title data-qa={ProductQA.RecommendedForYouTitle} $isPDP={isPDP}>
                {title}
              </Title>
              {!!buttonLabel && (
                <TabletAndDesktopButtonLink
                  qaAttr={ProductQA.DesktopRecommendedShopAllLink}
                  href="/sneakers"
                  color="grey"
                >
                  {buttonLabel}
                </TabletAndDesktopButtonLink>
              )}
            </TitleContainer>
            {recommendations?.length !== 0 && (
              <ProductGrid products={recommendations as unknown as IProduct[]} hidePrices={true} />
            )}
            {/* if there no recommendations to render, we render trending products instead
              Potential performance enhancement: Trending products is not server side rendered since
              its conditionally rendered based on recommendation availability, this will cause a
              re-render since it will differ from the server side render dom tree. Since this is an
              edge case (ie only when recs isn't working) we'll leave for now
            */}
            {(recommendations.length === 0 || error) &&
              (consumerSearchTrendingResults.length > 0 ? (
                <TrendingProducts
                  hits={consumerSearchTrendingResults || []}
                  hidePrices={true}
                />
              ) : (
                <Spinner threshold={0} showSpinner={true} />
              ))}
            {!!buttonLabel && (
              <MobileButtonLink
                data-qa={ProductQA.MobileRecommendedShopAllLink}
                href="/sneakers"
                buttonType="secondary"
                $fill
              >
                {buttonLabel}
              </MobileButtonLink>
            )}
          </GridContent>
        )}
      </Spinner>
    </Content>
  )
}

const Title = styled.h2<{ $isPDP?: boolean }>`
  ${fonts.HEADER_1}
  margin-top: 0;
  margin-bottom: 30px;
  ${({ $isPDP }) =>
    $isPDP
      ? css`
          ${fonts.HEADER_3};
          ${media.large`
            ${fonts.HEADER_1}
            margin-top: 0;
            margin-bottom: 30px;
          `}
        `
      : css`
          ${fonts.HEADER_1}
          margin-top: 0;
          margin-bottom: 30px;
        `};
`

const TitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
`
const Content = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 40px ${styles.mobilePageLayoutPadding};
`
const TabletAndDesktopButtonLink = styled(LinkCTA)`
  display: none;
  ${media.medium`
    display: block;
  `}
`
const MobileButtonLink = styled(Button)`
  margin-top: 40px;
  display: block;
  ${media.medium`
    display: none;
  `}
`
